import type React from "react";
import { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  CircularProgress,
  Button,
} from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import { BarChart } from "@mui/x-charts/BarChart";
import { useLocation, useParams } from "react-router-dom";
import { tokens } from "../../../theme";
import MuzukuruLogo from "../../../assets/Muzukuruimg1.png";
import backgroundPattern from "../../../assets/backgroundPattern.jpg";
import { Download, Refresh } from "@mui/icons-material";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import apiService from "../../../service/Api";
import type { IStreamReport } from "../types/stream-report";

const A4_WIDTH = 794;
const A4_HEIGHT = 1123;

const REGION_NAMES = new Intl.DisplayNames(["en"], { type: "region" });
const regionName = (code: string): string => {
  try {
    return REGION_NAMES.of(code) || code;
  } catch {
    return code;
  }
};

const StreamReport: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { state: routerState } = useLocation();
  const { id } = useParams();

  const state = routerState;

  const serviceType: string = state?.serviceType || "Funeral";
  const deceasedName: string | null = state?.deceasedName || null;
  const streamTitle: string | null = state?.streamTitle || null;
  const isFuneral = serviceType === "Funeral" || serviceType === "FuneralHome";

  // Derive a display name for the subject of the stream
  const subjectName = isFuneral
    ? deceasedName || "the deceased"
    : streamTitle || "this event";

  const printRef = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useState(false);

  const [report, setReport] = useState<IStreamReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reauthUrl, setReauthUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;

    let active = true;
    setLoading(true);
    setError(null);
    setReauthUrl(null);
    apiService
      .get<IStreamReport>(`/api/stream-reports/reports/fetch-stats/?stream=${id}`)
      .then((res) => {
        if (active) setReport(res.data);
      })
      .catch((err) => {
        console.error("Failed to fetch stream report", err);
        if (!active) return;
        const data = err?.response?.data;
        setError(
          data?.detail || "Statistics are not available for this stream yet."
        );
        if (data?.code === "reauth_required" && data?.reauth_url) {
          setReauthUrl(data.reauth_url);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id]);

  const PIE_PALETTE = [
    "#109618",
    "#FF9900",
    "#DC3912",
    "#3366CC",
    "#990099",
    "#0099C6",
    "#DD4477",
    "#66AA00",
    "#B82E2E",
    "#316395",
  ];

  const buildPieData = () => {
    const countries = report?.country ?? [];
    const total = countries.reduce((sum, c) => sum + c.views, 0);
    if (total === 0) return [];
    const sorted = [...countries].sort((a, b) => b.views - a.views);
    const head = sorted
      .slice(0, 5)
      .map((c) => ({ label: regionName(c.country), views: c.views }));
    const restViews = sorted
      .slice(5)
      .reduce((sum, c) => sum + c.views, 0);
    const rows = restViews > 0 ? [...head, { label: "Other", views: restViews }] : head;
    return rows.map((row, index) => ({
      id: index,
      value: Math.round((row.views / total) * 100),
      views: row.views,
      label: row.label,
      color: PIE_PALETTE[index % PIE_PALETTE.length],
    }));
  };

  const PIE_DATA = buildPieData();

  const VIEWS_DATA = [
    { category: "At Venue", value: report?.Physical_views_count ?? 0 },
    { category: "Online", value: report?.views_count ?? 0 },
  ];

  const COMMENTS_DATA = [
    { category: "Online Comments", value: report?.comments_count ?? 0 },
    { category: "Tribute Table", value: report?.tribute_comments ?? 0 },
  ];

  const totalViews = VIEWS_DATA.reduce((sum, item) => sum + item.value, 0);
  const totalComments = COMMENTS_DATA.reduce((sum, item) => sum + item.value, 0);

  const handleDownload = async () => {
    if (!printRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(printRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
      const pdf = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
      const imgData = canvas.toDataURL("image/png");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`Livestream-Report-${subjectName}.pdf`);
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!report) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
          minHeight: "60vh",
          px: 2,
        }}
      >
        <Typography color="text.secondary" sx={{ textAlign: "center", maxWidth: 480 }}>
          {error || "No statistics available for this stream."}
        </Typography>
        {reauthUrl && (
          <Button
            variant="contained"
            color="warning"
            startIcon={<Refresh />}
            onClick={() => {
              window.location.href = reauthUrl;
            }}
          >
            Re-authenticate YouTube
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ width: `${A4_WIDTH}px`, margin: "0 auto", p: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Tooltip title="Download PDF">
          <IconButton
            onClick={handleDownload}
            disabled={downloading}
            sx={{
              backgroundColor: colors.primary[400],
              color: colors.primary[900],
              width: 40,
              height: 40,
              "&:hover": { backgroundColor: colors.secondary[400] },
            }}
          >
            {downloading ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              <Download />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      <Paper
        ref={printRef}
        elevation={4}
        sx={{
          width: `${A4_WIDTH}px`,
          minHeight: `${A4_HEIGHT}px`,
          p: "12mm 10mm",
          pt: "20mm",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          backgroundColor: colors.primary[900],
          backgroundImage: `url(${backgroundPattern})`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          transform: isMobile ? "scale(0.45)" : "scale(1)",
          transformOrigin: "top center",
          "@media print": {
            boxShadow: "none",
            width: "210mm",
            minHeight: "297mm",
            p: "12mm 14mm",
            transform: "none",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            width: "100%",
            mb: "8mm",
            pb: "5mm",
          }}
        >
          <Box
            component="img"
            src={MuzukuruLogo}
            alt="Muzukuru Logo"
            sx={{
              width: 100,
              height: 100,
              objectFit: "contain",
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontWeight: 800,
              color: colors.primary[400],
              fontSize: "32px",
              lineHeight: 1.1,
              textTransform: "uppercase",
              fontFamily: "'Playfair Display', serif",
              letterSpacing: "0.5px",
            }}
          >
            Livestream Performance
            <br />
            Report
          </Typography>
        </Box>

        <Typography
          sx={{
            color: colors.primary[400],
            mb: "7mm",
            fontSize: "20px",
            textAlign: "left",
            fontWeight: 700,
            fontFamily: "sans-serif",
            alignSelf: "flex-start",
          }}
        >
          {isFuneral
            ? `This report gives an insight on how the livestream was viewed for ${subjectName.endsWith("s") ? `${subjectName}'` : `${subjectName}'s`
            } Funeral Service.`
            : `This report gives an insight on how the livestream was viewed for ${subjectName}.`}
        </Typography>

        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            mb: "6mm",
            gap: 2,
          }}
        >
          {PIE_DATA.length > 0 ? (
            <>
              {/* Custom vertical legend on the left */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  gap: "10px",
                  flex: "0 0 auto",
                  minWidth: 200,
                  pl: 3,
                }}
              >
                {PIE_DATA.map((item) => (
                  <Box
                    key={item.id}
                    sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor: item.color,
                        flexShrink: 0,
                      }}
                    />
                    <Box>
                      <Typography
                        sx={{
                          color: colors.primary[100],
                          fontSize: "15px",
                          fontWeight: 700,
                          lineHeight: 1.2,
                          fontFamily: "sans-serif",
                        }}
                      >
                        {item.label}
                      </Typography>
                      <Typography
                        sx={{
                          color: colors.primary[400],
                          fontSize: "13px",
                          lineHeight: 1.3,
                          fontWeight: 500,
                          fontFamily: "sans-serif",
                        }}
                      >
                        {item.views} views &middot; {item.value}%
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
                <PieChart
                  series={[
                    {
                      data: PIE_DATA,
                      innerRadius: 0,
                      outerRadius: 130,
                      paddingAngle: 0,
                    },
                  ]}
                  width={320}
                  height={300}
                  margin={{ top: 10, bottom: 10, left: 10, right: 10 }}
                  slots={{ legend: () => null }}
                />
              </Box>
            </>
          ) : (
            <Box
              sx={{
                height: 300,
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 4,
              }}
            >
              <Typography
                sx={{
                  color: colors.primary[400],
                  textAlign: "center",
                  fontSize: "14px",
                }}
              >
                Geographic viewer data is not available yet. YouTube Analytics can
                take a few days to finalise after a stream ends.
              </Typography>
            </Box>
          )}
        </Box>

        <Typography
          sx={{
            color: colors.primary[400],
            mb: "5mm",
            fontSize: "16px",
            textAlign: "left",
            alignSelf: "flex-start",
            fontWeight: 700,
            fontFamily: "sans-serif",
          }}
        >
          {isFuneral
            ? `People in different locations were able to watch the stream and join you in celebrating ${subjectName.endsWith("s") ? `${subjectName}'` : `${subjectName}'s`
            } life.`
            : `People in different locations were able to watch and participate in ${subjectName}.`}
        </Typography>

        <Box
          sx={{
            borderTop: `4px dotted ${colors.primary[500]}`,
            width: "100%",
            my: "4mm",
          }}
        />

        <Grid
          container
          spacing={4}
          justifyContent="center"
          sx={{ width: "100%" }}
        >
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography
              sx={{
                textAlign: "center",
                mb: 1,
                color: colors.primary[400],
                fontSize: "16px",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                fontFamily: "'Playfair Display', serif",
              }}
            >
              Views
            </Typography>
            <Typography
              sx={{
                textAlign: "center",
                fontSize: "14px",
                color: colors.primary[500],
                mb: 1,
              }}
            >
              Total: {totalViews}
            </Typography>
            <BarChart
              dataset={VIEWS_DATA}
              yAxis={[{ scaleType: "band", dataKey: "category", width: 120 }]}
              series={[
                {
                  dataKey: "value",
                  color: colors.primary[400],
                  valueFormatter: (value) =>
                    value != null && totalViews > 0
                      ? `${value} (${Math.round((value / totalViews) * 100)}%)`
                      : `${value ?? 0} (0%)`,
                },
              ]}
              layout="horizontal"
              height={160}
              width={380}
              margin={{ left: 20, right: 60, top: 10, bottom: 20 }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography
              sx={{
                textAlign: "center",
                mb: 1,
                color: colors.primary[400],
                fontSize: "16px",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                fontFamily: "'Playfair Display', serif",
              }}
            >
              Comments Received
            </Typography>
            <Typography
              sx={{
                textAlign: "center",
                fontSize: "14px",
                color: colors.primary[500],
                mb: 1,
              }}
            >
              Total: {totalComments}
            </Typography>
            <BarChart
              dataset={COMMENTS_DATA}
              yAxis={[{ scaleType: "band", dataKey: "category", width: 120 }]}
              series={[
                {
                  dataKey: "value",
                  color: colors.primary[500],
                  valueFormatter: (value) =>
                    value != null && totalComments > 0
                      ? `${value} (${Math.round((value / totalComments) * 100)}%)`
                      : `${value ?? 0} (0%)`,
                },
              ]}
              layout="horizontal"
              height={160}
              width={380}
              margin={{ left: 20, right: 60, top: 10, bottom: 20 }}
            />
          </Grid>
        </Grid>

        <Box
          sx={{
            borderTop: `4px dotted ${colors.primary[500]}`,
            width: "100%",
            my: "4mm",
          }}
        />
      </Paper>
    </Box>
  );
};

export default StreamReport;